import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import IconSvg from 'src/components/common/ui/IconSvg';
import { useHttp, useNotification } from 'src/hooks';
import PasswordStrengthIndicator from 'src/components/common/ui/PasswordStrengthIndicator';

const NewPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendRequest, isLoading, error } = useHttp();
  const notify = useNotification();

  const { email, otp } = location.state || { email: '', otp: '' };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Password is required.')
        .min(8, 'Password must be at least 8 characters long.')
        .matches(/(?=.*[a-z])/, 'Password must contain at least one lowercase letter.')
        .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter.')
        .matches(/(?=.*[0-9])/, 'Password must contain at least one number.')
        .matches(/(?=.*[!@#$%^&*])/, 'Password must contain at least one special character.'),
      confirmPassword: Yup.string()
        .required('Confirm password is required.')
        .oneOf([Yup.ref('password')], 'Passwords must match.'),
    }),
    onSubmit: values => {
      resetPassword(values.password);
    },
  });

  const isCreateButtonDisabled =
    !formik.values.password ||
    !formik.values.confirmPassword ||
    !!formik.errors.password ||
    !!formik.errors.confirmPassword;

  const resetPassword = (password: string) => {
    if (!email) {
      notify('Please enter an email first.', 'error');
      return;
    }

    const otpString = otp.join('');

    sendRequest(
      {
        method: 'POST',
        url: `auth/reset-password`,
        payload: { email, otp: parseInt(otpString), password },
      },
      data => {
        notify(data.message, 'success');
        navigate('/signin');
      },
    );
  };
  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);

  return (
    <div className="lg:w-1/2 w-full lg:p-8 flex flex-col items-start">
      <IconSvg icon="appLogo" className="cursor-pointer lg:w-auto w-24" onClick={() => navigate('/')} alt="Logo" />

      <p className="lg:text-[36px] text-[28px] font-semibold text-black font-inter mt-8 lg:mb-6 mb-4">
        New password
      </p>
      <p className="text-[#4A4D4F] lg:text-xl text-base font-normal font-inter mb-6">
        Out with the old, in with the new—let’s get your password sorted!
      </p>
      <form className="w-full" onSubmit={formik.handleSubmit}>
        {/* Password Field */}
        <div className="mb-4 relative">
          <label className="flex items-center justify-between text-[#4A4D4F] lg:text-lg text-base font-normal mb-2">
            <span>
              Password <span className="text-[#CC0E3C]">*</span>
            </span>
            <div className="ml-2">
              <PasswordStrengthIndicator password={formik.values.password} />
            </div>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Enter password"
              className="w-full px-4 py-2 border rounded-lg placeholder-grayscale-600 lg:text-base text-sm font-inter font-normal outline-none"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
            >
              {showPassword ? (
                <IconSvg
                  icon="hidePasswordEye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              ) : (
                <IconSvg
                  icon="eye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              )}
            </span>
          </div>
          {formik.touched.password && formik.errors.password && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.password}</p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="mb-6 relative">
          <label className="block text-[#4A4D4F] lg:text-lg text-base font-normal mb-2">
            Confirm password <span className="text-[#CC0E3C]">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm password"
              className="w-full px-4 py-2 border rounded-lg placeholder-grayscale-600 lg:text-base text-sm font-inter font-normal outline-none"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
            >
              {showConfirmPassword ? (
                <IconSvg
                  icon="hidePasswordEye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              ) : (
                <IconSvg
                  icon="eye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              )}
            </span>
          </div>
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</p>
          )}
        </div>

        {/* Create Button */}
        <button
          type="submit"
          disabled={isCreateButtonDisabled || isLoading}
          className={`w-full py-4 rounded-lg lg:text-lg text-sm font-semibold ${isCreateButtonDisabled || isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white'
            }`}
        >
          {isLoading ? 'Processing...' : 'Create'}
        </button>
      </form>
    </div>
  );
};

export default NewPasswordForm;
